import { Decoration, WidgetType, type EditorView } from "@codemirror/view";
import type { EditorState } from "@codemirror/state";
import type { LivePreviewCursor } from "./cursor";
import type { Tree } from "@lezer/common";

interface DecorationEntry {
  from: number;
  to: number;
  decoration: Decoration;
}

class BulletWidget extends WidgetType {
  toDOM(): HTMLElement {
    const span = document.createElement("span");
    span.className = "cm-md-bullet";
    span.textContent = "\u2022 "; // bullet + trailing space
    return span;
  }

  eq(): boolean {
    return true;
  }
}

class NumberWidget extends WidgetType {
  constructor(private num: string) {
    super();
  }

  toDOM(): HTMLElement {
    const span = document.createElement("span");
    span.className = "cm-md-number";
    span.textContent = this.num + " "; // number + trailing space
    return span;
  }

  eq(other: NumberWidget): boolean {
    return this.num === other.num;
  }
}

class CheckboxWidget extends WidgetType {
  constructor(
    private checked: boolean,
    private pos: number,
  ) {
    super();
  }

  toDOM(view: EditorView): HTMLElement {
    const wrapper = document.createElement("span");
    wrapper.style.cssText = "display:inline-flex;align-items:center;gap:4px;margin-right:4px;";

    const box = document.createElement("span");
    box.className = "cm-md-checkbox" + (this.checked ? " checked" : "");
    if (this.checked) {
      box.innerHTML =
        '<svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="1.5 5.5 4 8 8.5 2"></polyline></svg>';
    }

    wrapper.appendChild(box);
    wrapper.addEventListener("mousedown", (e) => {
      e.preventDefault();
      e.stopPropagation();
      const char = this.checked ? " " : "x";
      view.dispatch({
        changes: { from: this.pos, to: this.pos + 1, insert: char },
      });
    });
    return wrapper;
  }

  eq(other: CheckboxWidget): boolean {
    return this.checked === other.checked && this.pos === other.pos;
  }

  ignoreEvent(): boolean {
    return false;
  }
}

/**
 * Build decorations for bullet lists, ordered lists, and checkboxes.
 *
 * Uses per-block reveal: when the cursor is on ANY line of a BulletList
 * or OrderedList node, the entire list block is shown as raw markdown.
 */
export function buildListDecorations(
  state: EditorState,
  tree: Tree,
  range: { from: number; to: number },
  cursor: LivePreviewCursor,
  decorations: DecorationEntry[],
): void {
  tree.iterate({
    from: range.from,
    to: range.to,
    enter(node) {
      if (node.name === "BulletList") {
        buildBulletListDecorations(state, node, cursor, decorations);
        return false;
      }

      if (node.name === "OrderedList") {
        buildOrderedListDecorations(state, node, cursor, decorations);
        return false;
      }
    },
  });
}

function isCursorInBlock(
  state: EditorState,
  blockFrom: number,
  blockTo: number,
  cursor: LivePreviewCursor,
): boolean {
  const blockFirstLine = state.doc.lineAt(blockFrom).number;
  const blockLastLine = state.doc.lineAt(blockTo).number;
  const cursorLine = cursor.cursorLine;

  if (cursorLine >= blockFirstLine && cursorLine <= blockLastLine) return true;

  const anchorLine = cursor.anchorLine;
  if (anchorLine >= blockFirstLine && anchorLine <= blockLastLine) return true;

  return false;
}

function buildBulletListDecorations(
  state: EditorState,
  node: { from: number; to: number; node: any },
  cursor: LivePreviewCursor,
  decorations: DecorationEntry[],
): void {
  if (isCursorInBlock(state, node.from, node.to, cursor)) return;

  // Process this list's direct ListItem children
  const childCursor = node.node.cursor();
  if (!childCursor.firstChild()) return;

  do {
    if (childCursor.name === "ListItem") {
      buildBulletItemDecorations(state, childCursor, decorations);
      // Recurse into nested BulletLists within this ListItem
      processNestedLists(state, childCursor.node, cursor, decorations);
    }
  } while (childCursor.nextSibling());
}

/**
 * Recurse into a ListItem's children to find and process nested lists.
 * A ListItem can contain nested BulletList or OrderedList nodes.
 */
function processNestedLists(
  state: EditorState,
  listItemNode: any,
  cursor: LivePreviewCursor,
  decorations: DecorationEntry[],
): void {
  const c = listItemNode.cursor();
  if (!c.firstChild()) return;

  do {
    if (c.name === "BulletList") {
      buildBulletListDecorations(
        state,
        { from: c.from, to: c.to, node: c.node },
        cursor,
        decorations,
      );
    } else if (c.name === "OrderedList") {
      buildOrderedListDecorations(
        state,
        { from: c.from, to: c.to, node: c.node },
        cursor,
        decorations,
      );
    }
  } while (c.nextSibling());
}

/**
 * Find a named node anywhere in the subtree (recursive).
 * The Lezer markdown tree nests TaskMarker inside Paragraph inside ListItem,
 * so a simple firstChild() loop misses it.
 */
function findDescendant(syntaxNode: any, name: string): { from: number; to: number } | null {
  const c = syntaxNode.cursor();
  if (!c.firstChild()) return null;

  do {
    if (c.name === name) {
      return { from: c.from, to: c.to };
    }
    // Recurse
    const found = findDescendant(c.node, name);
    if (found) return found;
  } while (c.nextSibling());

  return null;
}

function buildBulletItemDecorations(
  state: EditorState,
  listItemCursor: any,
  decorations: DecorationEntry[],
): void {
  const itemNode = listItemCursor.node;

  // Find ListMark (direct child: "- " or "* ")
  let listMarkFrom = -1;
  let listMarkTo = -1;

  const inner = itemNode.cursor();
  if (inner.firstChild()) {
    do {
      if (inner.name === "ListMark") {
        listMarkFrom = inner.from;
        listMarkTo = inner.to;
        break;
      }
    } while (inner.nextSibling());
  }

  if (listMarkFrom === -1) return;

  // Find TaskMarker anywhere in the subtree (it's nested inside Paragraph)
  const taskMarker = findDescendant(itemNode, "TaskMarker");

  if (taskMarker) {
    // Checkbox item: replace from ListMark start to after TaskMarker + all trailing spaces
    let replaceEnd = taskMarker.to;
    while (state.doc.sliceString(replaceEnd, replaceEnd + 1) === " ") {
      replaceEnd++;
    }

    // Skip empty checkbox items
    const itemLine = state.doc.lineAt(listMarkFrom);
    if (replaceEnd >= itemLine.to) return;

    const text = state.doc.sliceString(taskMarker.from, taskMarker.to);
    const taskChecked = text.includes("x") || text.includes("X");
    const togglePos = taskMarker.from + 1;

    decorations.push({
      from: listMarkFrom,
      to: replaceEnd,
      decoration: Decoration.replace({
        widget: new CheckboxWidget(taskChecked, togglePos),
      }),
    });

    // Strikethrough for checked items
    if (taskChecked && replaceEnd < itemNode.to) {
      const itemLine = state.doc.lineAt(replaceEnd);
      const textEnd = Math.min(itemLine.to, itemNode.to);
      if (replaceEnd < textEnd) {
        decorations.push({
          from: replaceEnd,
          to: textEnd,
          decoration: Decoration.mark({
            class: "cm-md-checkbox-text-done",
          }),
        });
      }
    }
  } else {
    // Regular bullet: replace ListMark + all trailing spaces
    let hideEnd = listMarkTo;
    while (state.doc.sliceString(hideEnd, hideEnd + 1) === " ") {
      hideEnd++;
    }

    // Skip empty items
    const itemLine = state.doc.lineAt(listMarkFrom);
    if (hideEnd >= itemLine.to) return;

    decorations.push({
      from: listMarkFrom,
      to: hideEnd,
      decoration: Decoration.replace({
        widget: new BulletWidget(),
      }),
    });
  }
}

function buildOrderedListDecorations(
  state: EditorState,
  node: { from: number; to: number; node: any },
  cursor: LivePreviewCursor,
  decorations: DecorationEntry[],
): void {
  if (isCursorInBlock(state, node.from, node.to, cursor)) return;

  const childCursor = node.node.cursor();
  if (!childCursor.firstChild()) return;

  do {
    if (childCursor.name === "ListItem") {
      buildOrderedItemDecorations(state, childCursor, decorations);
      // Recurse into nested lists within this ListItem
      processNestedLists(state, childCursor.node, cursor, decorations);
    }
  } while (childCursor.nextSibling());
}

function buildOrderedItemDecorations(
  state: EditorState,
  listItemCursor: any,
  decorations: DecorationEntry[],
): void {
  const itemNode = listItemCursor.node;
  const inner = itemNode.cursor();
  if (!inner.firstChild()) return;

  do {
    if (inner.name === "ListMark") {
      const markText = state.doc.sliceString(inner.from, inner.to);
      let hideEnd = inner.to;
      while (state.doc.sliceString(hideEnd, hideEnd + 1) === " ") {
        hideEnd++;
      }

      // Skip empty numbered items
      const itemLine = state.doc.lineAt(inner.from);
      if (hideEnd >= itemLine.to) break;

      decorations.push({
        from: inner.from,
        to: hideEnd,
        decoration: Decoration.replace({
          widget: new NumberWidget(markText),
        }),
      });
      break;
    }
  } while (inner.nextSibling());
}
